import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { C, KPICard, SectionWrapper } from './DashboardShared';
import { getPortfolioOverviewKpiApi } from '../apiService';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus, getGlobalCookie } from '../../../utils';
import KpiCardSkeleton from './KPICardSkeleton';
import appConfig from '../../../config';

function parseLessorAccess() {
  try {
    const raw = getGlobalCookie('lessorAccess');
    if (raw == null || raw === '') return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getEngineCountFromBreakdown(breakdown) {
  if (!breakdown || typeof breakdown !== 'object') return null;
  for (const key of Object.keys(breakdown)) {
    if (String(key).toLowerCase() !== 'engine') continue;
    const v = breakdown[key];
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function normalizeKpiLabel(label) {
  const raw = String(label ?? '').trim();
  if (!raw) return label;

  if (/^total\s*aircraft(?:\(s\)|s)?$/i.test(raw)) return 'TOTAL AIRCRAFT(S)';
  if (/^total\s*engine(?:\(s\)|s)?$/i.test(raw)) return 'TOTAL ENGINE(S)';
  if (/^active\s*lease(?:\(s\)|s)?$/i.test(raw)) return 'ACTIVE LEASE(S)';
  if (/^active\s*leases$/i.test(raw)) return 'ACTIVE LEASE(S)';

  return label;
}

/** Engine-only lessor: no KPI breakdown; Total Aircraft label → Total Engine; fleet total & Active Leases main values use breakdown.engine, not rolled-up value. */
function mapPortfolioKpisForLessor(kpis, isEngineOnlyLessor) {
  if (!Array.isArray(kpis)) return [];
  if (!isEngineOnlyLessor) {
    return kpis.map((kpi) => {
      if (!kpi || typeof kpi !== 'object') return kpi;
      return { ...kpi, label: normalizeKpiLabel(kpi.label) };
    });
  }
  return kpis.map((kpi) => {
    if (!kpi || typeof kpi !== 'object') return kpi;
    const { breakdown, ...rest } = kpi;
    let { label, value } = kpi;
    const isFleetTotalKpi =
      typeof label === 'string' &&
      (/total\s*aircraft/i.test(label) || /^total\s*engine$/i.test(label.trim()));
    const isActiveLeasesKpi =
      typeof kpi.label === 'string' && /^active\s*leases$/i.test(String(kpi.label).trim());

    if (isFleetTotalKpi && /total\s*aircraft/i.test(label)) {
      label = label.replace(/total\s*aircraft/gi, 'Total Engine');
    }

    if (isFleetTotalKpi || isActiveLeasesKpi) {
      const engineCount = getEngineCountFromBreakdown(breakdown);
      if (engineCount != null) {
        value = String(engineCount);
      }
    }
    return { ...rest, label: normalizeKpiLabel(label), value, breakdown: undefined };
  });
}

const PortfolioOverview = () => {
  const [kpis, setKpis] = useState([]);
  const [asOf, setAsOf] = useState('');
  const [aircraftCount, setAircraftCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const isLessorMatched = useMemo(() => {
    const lessorAccess = parseLessorAccess();
    return appConfig.lessorIds?.includes(lessorAccess?.id) ?? false;
  }, []);

  const displayKpis = useMemo(
    () => mapPortfolioKpisForLessor(kpis, isLessorMatched),
    [kpis, isLessorMatched],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    globalGetService(getPortfolioOverviewKpiApi())
      .then((response) => {
        if (cancelled) return;
        if (!checkApiStatus(response)) {
          setAsOf('');
          setAircraftCount(0);
          setKpis([]);
          return;
        }
        const raw = response?.data?.data;
        const p = raw && typeof raw === 'object' ? raw : null;
        if (!p) {
          setAsOf('');
          setAircraftCount(0);
          setKpis([]);
          return;
        }
        setAsOf(p.asOf ?? '');
        setAircraftCount(Number(p.aircraftCount) || 0);
        setKpis(Array.isArray(p.kpis) ? p.kpis : []);
      })
      .catch(() => {
        if (cancelled) return;
        setAsOf('');
        setAircraftCount(0);
        setKpis([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const hasAsOf = String(asOf ?? '').trim() !== '';
  const hasAircraftCount = aircraftCount !== null && aircraftCount !== undefined && !Number.isNaN(Number(aircraftCount));
  const hasKpis = kpis.length > 0;
  const subtitle =
    !loading && hasKpis && hasAsOf && (isLessorMatched || hasAircraftCount)
      ? isLessorMatched
        ? `As of ${String(asOf).trim()}`
        : `As of ${String(asOf).trim()} · ${aircraftCount} aircraft`
      : undefined;

  const kpiGridSx = {
    display: 'grid',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    gap: '12px',
    alignItems: 'stretch',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      sm: 'repeat(3, minmax(0, 1fr))',
      md: 'repeat(4, minmax(0, 1fr))',
    },
  };

  return (
    <SectionWrapper title="Fleet Portfolio Overview" subtitle={subtitle}>
      {loading ? (
        <Box sx={kpiGridSx}>
          {Array.from({ length: 4 }, (_, i) => (
            <Box key={`sk-${i}`} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
              <KpiCardSkeleton />
            </Box>
          ))}
        </Box>
      ) : hasKpis ? (
        <Box sx={kpiGridSx}>
          {displayKpis.map((kpi, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
              <KPICard {...kpi} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 5, px: 2, textAlign: 'center', border: `1px dashed ${C.border}`, borderRadius: '12px', backgroundColor: C.surface2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, }} >
          <QueryStatsIcon sx={{ fontSize: 36, color: C.textMuted, opacity: 0.5 }} />
          <Typography variant="body2" sx={{ color: C.textMuted, fontWeight: 500 }}>
            No data found
          </Typography>
        </Box>
      )}
    </SectionWrapper>
  );
};

export default PortfolioOverview;
